import React, { useState, useEffect } from 'react';
import { api } from '../../../utils/api';
import { toast } from 'react-toastify';
import {
  TrendingDown, DollarSign, Calendar, Plus, Trash2, Edit2, Check, X,
  Lightbulb, Droplet, Flame, Globe, CreditCard, RefreshCw, Loader2
} from 'lucide-react';

interface UtilityBill {
  id: number;
  type: string;
  billed_at: string;
  amount: number;
  status: 'pagado' | 'pendiente';
  notes: string | null;
}

const TYPE_LABELS: Record<string, { label: string; icon: any; color: string; bg: string }> = {
  agua:     { label: '💧 Agua',             icon: Droplet,   color: 'text-blue-600', bg: 'bg-blue-50' },
  energia:  { label: '💡 Energía / Luz',    icon: Lightbulb, color: 'text-amber-500', bg: 'bg-amber-50' },
  gas:      { label: '🔥 Gas',              icon: Flame,     color: 'text-red-500', bg: 'bg-red-50' },
  internet: { label: '🌐 Internet / Tel',   icon: Globe,     color: 'text-indigo-650', bg: 'bg-indigo-50' },
  otros:    { label: '📦 Otros servicios',  icon: CreditCard,color: 'text-text-muted', bg: 'bg-muted/40' },
};

const fmt = (n: number) =>
  new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 }).format(n);

const formatDate = (dateStr: string | null) => {
  if (!dateStr) return '—';
  const datePart = dateStr.includes('T') ? dateStr.split('T')[0] : dateStr;
  const [y, m, d] = datePart.split('-');
  if (!y || !m || !d) return datePart;
  return `${d}/${m}/${y}`;
};

export default function UtilitiesPage() {
  const [bills, setBills] = useState<UtilityBill[]>([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingBill, setEditingBill] = useState<UtilityBill | null>(null);
  
  const [form, setForm] = useState({
    type: 'energia',
    billed_at: new Date().toISOString().slice(0, 10),
    amount: 0,
    status: 'pendiente' as 'pagado' | 'pendiente',
    notes: ''
  });

  const [filters, setFilters] = useState({
    type: '',
    status: '',
    from: '',
    to: ''
  });

  const loadBills = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/purchases/utility-bills', { params: filters });
      setBills(data);
    } catch (e) {
      toast.error('Error al cargar recibos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadBills();
  }, [filters]);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (form.amount <= 0) return toast.error('El monto debe ser mayor a 0');

    try {
      if (editingBill) {
        await api.put(`/purchases/utility-bills/${editingBill.id}`, form);
        toast.success('Recibo actualizado');
      } else {
        await api.post('/purchases/utility-bills', form);
        toast.success('Recibo registrado correctamente');
      }
      setShowModal(false);
      setEditingBill(null);
      setForm({
        type: 'energia',
        billed_at: new Date().toISOString().slice(0, 10),
        amount: 0,
        status: 'pendiente',
        notes: ''
      });
      loadBills();
    } catch (err) {
      toast.error('Error al guardar recibo');
    }
  };

  const handleToggleStatus = async (bill: UtilityBill) => {
    const nextStatus = bill.status === 'pagado' ? 'pendiente' : 'pagado';
    try {
      await api.put(`/purchases/utility-bills/${bill.id}`, {
        type: bill.type,
        billed_at: bill.billed_at,
        amount: bill.amount,
        status: nextStatus,
        notes: bill.notes
      });
      toast.success(`Recibo marcado como ${nextStatus}`);
      loadBills();
    } catch (err) {
      toast.error('Error al actualizar estado');
    }
  };

  const handleEdit = (bill: UtilityBill) => {
    setEditingBill(bill);
    setForm({
      type: bill.type,
      billed_at: bill.billed_at,
      amount: bill.amount,
      status: bill.status,
      notes: bill.notes ?? ''
    });
    setShowModal(true);
  };

  const handleDelete = async (id: number) => {
    if (!confirm('¿Estás seguro de eliminar este recibo?')) return;
    try {
      await api.delete(`/purchases/utility-bills/${id}`);
      toast.success('Recibo eliminado');
      loadBills();
    } catch (err) {
      toast.error('Error al eliminar recibo');
    }
  };

  // KPIs
  const totalPaid = bills.filter(b => b.status === 'pagado').reduce((s, b) => s + b.amount, 0);
  const totalPending = bills.filter(b => b.status === 'pendiente').reduce((s, b) => s + b.amount, 0);

  return (
    <div className="min-h-screen bg-bg px-4 py-6 md:p-8">
      <div className="mx-auto max-w-6xl space-y-6">

        {/* Header */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-text">Servicios Públicos</h1>
            <p className="text-sm text-text-muted">Control de recibos de agua, energía, gas, internet y otros gastos fijos</p>
          </div>
          <button
            onClick={() => {
              setEditingBill(null);
              setForm({
                type: 'energia',
                billed_at: new Date().toISOString().slice(0, 10),
                amount: 0,
                status: 'pendiente',
                notes: ''
              });
              setShowModal(true);
            }}
            className="inline-flex h-10 items-center gap-2 rounded-xl bg-primary px-4 text-sm font-bold text-white shadow hover:opacity-90 transition self-start sm:self-auto"
          >
            <Plus size={16} /> Registrar Recibo
          </button>
        </div>

        {/* KPI Row */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
          <div className="flex items-center gap-4 rounded-2xl border border-border bg-surface p-5 shadow-card">
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-red-100 text-red-600 bg-red-50">
              <TrendingDown size={20} />
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-text-muted">Pendiente de Pago</p>
              <p className="text-2xl font-bold text-red-650 text-red-600">{fmt(totalPending)}</p>
            </div>
          </div>
          <div className="flex items-center gap-4 rounded-2xl border border-border bg-surface p-5 shadow-card">
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-emerald-100 text-emerald-600 bg-emerald-50">
              <Check size={20} />
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-text-muted">Pagado (Filtrado)</p>
              <p className="text-2xl font-bold text-emerald-600">{fmt(totalPaid)}</p>
            </div>
          </div>
          <div className="flex items-center gap-4 rounded-2xl border border-border bg-surface p-5 shadow-card col-span-1 sm:col-span-2 md:col-span-1">
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-blue-50">
              <DollarSign size={20} className="text-blue-600" />
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-text-muted">Total General</p>
              <p className="text-2xl font-bold text-text">{fmt(totalPending + totalPaid)}</p>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-3 rounded-2xl border border-border bg-surface p-4 shadow-card">
          <select
            value={filters.type}
            onChange={e => setFilters(f => ({ ...f, type: e.target.value }))}
            className="rounded-xl border border-border bg-bg px-3 py-2 text-sm text-text focus:outline-none focus:ring-2 focus:ring-primary/50"
            aria-label="Tipo"
          >
            <option value="">Todos los servicios</option>
            <option value="agua">Agua</option>
            <option value="energia">Energía / Luz</option>
            <option value="gas">Gas</option>
            <option value="internet">Internet / Teléfono</option>
            <option value="otros">Otros</option>
          </select>

          <select
            value={filters.status}
            onChange={e => setFilters(f => ({ ...f, status: e.target.value }))}
            className="rounded-xl border border-border bg-bg px-3 py-2 text-sm text-text focus:outline-none focus:ring-2 focus:ring-primary/50"
            aria-label="Estado"
          >
            <option value="">Todos los estados</option>
            <option value="pagado">Pagado</option>
            <option value="pendiente">Pendiente</option>
          </select>

          <input
            type="date"
            value={filters.from}
            onChange={e => setFilters(f => ({ ...f, from: e.target.value }))}
            className="rounded-xl border border-border bg-bg px-3 py-2 text-sm text-text focus:outline-none focus:ring-2 focus:ring-primary/50"
            placeholder="Desde"
            aria-label="Desde"
          />
          <input
            type="date"
            value={filters.to}
            onChange={e => setFilters(f => ({ ...f, to: e.target.value }))}
            className="rounded-xl border border-border bg-bg px-3 py-2 text-sm text-text focus:outline-none focus:ring-2 focus:ring-primary/50"
            placeholder="Hasta"
            aria-label="Hasta"
          />

          {(filters.type || filters.status || filters.from || filters.to) && (
            <button
              onClick={() => setFilters({ type: '', status: '', from: '', to: '' })}
              className="text-xs font-bold text-primary hover:underline ml-auto"
            >
              Limpiar Filtros
            </button>
          )}
        </div>

        {/* List Table */}
        {loading ? (
          <div className="flex h-40 items-center justify-center text-text-muted">Cargando recibos...</div>
        ) : bills.length === 0 ? (
          <div className="rounded-2xl border border-border bg-surface p-10 text-center text-text-muted">
            No se encontraron recibos de servicios públicos.
          </div>
        ) : (
          <div className="overflow-hidden rounded-2xl border border-border bg-surface shadow-card">
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="border-b border-border bg-muted/30 text-xs font-semibold uppercase tracking-wider text-text-muted">
                    <th className="px-5 py-4">Servicio</th>
                    <th className="px-5 py-4">Fecha Factura / Gasto</th>
                    <th className="px-5 py-4 text-right">Monto</th>
                    <th className="px-5 py-4 text-center">Estado</th>
                    <th className="px-5 py-4">Notas</th>
                    <th className="px-5 py-4 text-center">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border text-sm text-text">
                  {bills.map(bill => {
                    const cfg = TYPE_LABELS[bill.type] || TYPE_LABELS.otros;
                    const Icon = cfg.icon;
                    return (
                      <tr key={bill.id} className="hover:bg-muted/10 transition">
                        <td className="px-5 py-4 font-semibold">
                          <div className="flex items-center gap-3">
                            <div className={`flex h-9 w-9 items-center justify-center rounded-lg ${cfg.bg} ${cfg.color}`}>
                              <Icon size={16} />
                            </div>
                            <span>{cfg.label}</span>
                          </div>
                        </td>
                        <td className="px-5 py-4 whitespace-nowrap">{formatDate(bill.billed_at)}</td>
                        <td className="px-5 py-4 text-right font-bold text-primary">{fmt(bill.amount)}</td>
                        <td className="px-5 py-4 text-center">
                          <button
                            onClick={() => handleToggleStatus(bill)}
                            className={`px-3 py-1 rounded-full text-xs font-bold transition border ${
                              bill.status === 'pagado'
                                ? 'bg-emerald-100 border-emerald-300 text-emerald-800'
                                : 'bg-red-100 border-red-300 text-red-800 hover:bg-red-200'
                            }`}
                          >
                            {bill.status === 'pagado' ? 'Pagado' : 'Pendiente'}
                          </button>
                        </td>
                        <td className="px-5 py-4 text-xs text-text-muted max-w-[220px] truncate">{bill.notes || '—'}</td>
                        <td className="px-5 py-4 text-center">
                          <div className="flex items-center justify-center gap-2">
                            <button
                              onClick={() => handleEdit(bill)}
                              className="text-text-muted p-1.5 rounded hover:bg-muted transition"
                              title="Editar"
                            >
                              <Edit2 size={14} />
                            </button>
                            <button
                              onClick={() => handleDelete(bill.id)}
                              className="text-red-500 p-1.5 rounded hover:bg-red-50 transition"
                              title="Borrar"
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Modal */}
        {showModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
            <div className="w-full max-w-md rounded-2xl bg-surface p-6 shadow-2xl border border-border animate-in fade-in zoom-in-95">
              <div className="flex items-center justify-between border-b border-border pb-3 mb-4">
                <h2 className="text-lg font-bold text-text">{editingBill ? 'Editar Recibo' : 'Registrar Recibo'}</h2>
                <button onClick={() => setShowModal(false)} className="rounded-lg p-1 text-text-muted hover:bg-muted">
                  <X size={18} />
                </button>
              </div>
              <form onSubmit={handleSave} className="space-y-4">
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-text-muted mb-1">Servicio / Tipo</label>
                  <select
                    value={form.type}
                    onChange={e => setForm(prev => ({ ...prev, type: e.target.value }))}
                    className="w-full rounded-xl border border-border bg-bg px-3 py-2.5 text-sm text-text focus:outline-none focus:ring-2 focus:ring-primary/50"
                  >
                    <option value="energia">Energía / Luz</option>
                    <option value="agua">Agua</option>
                    <option value="gas">Gas</option>
                    <option value="internet">Internet / Teléfono</option>
                    <option value="otros">Otros fijos</option>
                  </select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-text-muted mb-1">Fecha Recibo / Gasto</label>
                    <input
                      type="date"
                      required
                      value={form.billed_at}
                      onChange={e => setForm(prev => ({ ...prev, billed_at: e.target.value }))}
                      className="w-full rounded-xl border border-border bg-bg px-3.5 py-2.5 text-sm text-text focus:outline-none focus:ring-2 focus:ring-primary/50"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-text-muted mb-1">Monto (COP)</label>
                    <input
                      type="number"
                      required
                      min="1"
                      value={form.amount}
                      onChange={e => setForm(prev => ({ ...prev, amount: Number(e.target.value) }))}
                      className="w-full rounded-xl border border-border bg-bg px-3.5 py-2.5 text-sm font-bold text-primary focus:outline-none focus:ring-2 focus:ring-primary/50"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-text-muted mb-1">Estado</label>
                  <select
                    value={form.status}
                    onChange={e => setForm(prev => ({ ...prev, status: e.target.value as 'pagado' | 'pendiente' }))}
                    className="w-full rounded-xl border border-border bg-bg px-3 py-2.5 text-sm text-text focus:outline-none focus:ring-2 focus:ring-primary/50"
                  >
                    <option value="pendiente">Pendiente de Pago</option>
                    <option value="pagado">Ya Pagado</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-text-muted mb-1">Observaciones</label>
                  <input
                    type="text"
                    value={form.notes}
                    placeholder="Ej: Consumo mes de Julio"
                    onChange={e => setForm(prev => ({ ...prev, notes: e.target.value }))}
                    className="w-full rounded-xl border border-border bg-bg px-3.5 py-2.5 text-sm text-text focus:outline-none focus:ring-2 focus:ring-primary/50"
                  />
                </div>

                <div className="flex justify-end gap-2 border-t border-border pt-4 mt-6">
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="h-10 rounded-xl px-4 text-sm font-semibold text-text-muted hover:bg-muted"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="h-10 rounded-xl bg-primary px-5 text-sm font-bold text-white shadow hover:opacity-90"
                  >
                    Guardar
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
